import {Product} from '@/_application/modules/products/model/product';
import {Swiper, SwiperSlide} from 'swiper/react';
import {useCrossSellProducts} from '../hooks/use-cross-sell-products';
import ProductComboSummary from './product-combo-summary.desktop';
import ProductCrossSellItem from './product-cross-sell-item.desktop';
import {useCrossSellProductBySku} from '@/framework/product';
import {useCallback, useMemo, useRef} from 'react';
import {useSession} from 'next-auth/react';
import {useModalAction} from '@/_application/core/components/ui/modal/modal.context';
import {GiftsSelectorWidget} from '@kidslabs/module-flash-sale';
import type {GiftsSelectorWidgetRef} from '@kidslabs/module-flash-sale';
import {useProductGiftHook} from '@kidslabs/module-flash-sale';

interface IProductCrossSellProps {
    mainProduct: Product;
}

const ProductCrossSell = ({mainProduct}: IProductCrossSellProps) => {
    const {crossSellProducts} = useCrossSellProductBySku({sku: mainProduct?.sku!});
    const {status: sessionStatus} = useSession();
    const {openModal} = useModalAction();

    const simpleCrossSellProducts = useMemo(() => {
        return crossSellProducts?.filter(product => product.__typename === 'SimpleProduct') ?? [];
    }, [crossSellProducts]);

    const {
        totalSavings,
        selectedProducts,
        totalBasePrice,
        totalPrice,
        onSubmit,
        handleCheckboxChange,
        isAddToCartLoading,
    } = useCrossSellProducts({mainProduct, data: simpleCrossSellProducts});

    // Gift handling for the main product
    const giftsSelectorRef = useRef<GiftsSelectorWidgetRef | null>(null);
    const {sortedGiftsDataList} = useProductGiftHook({
        product: mainProduct,
        observeStore: true,
    });

    // Authentication guard + gift flow + buy-now execution
    // Check sortedGiftsDataList.length at click time (not render time)
    // because gift data loads asynchronously from the store
    const handleBuyNowClick = useCallback(() => {
        if (sessionStatus !== 'authenticated') {
            openModal('OTP_LOGIN');
            return;
        }

        if (sortedGiftsDataList.length > 0 && giftsSelectorRef.current) {
            giftsSelectorRef.current.openBottomSheet();
        } else {
            onSubmit();
        }
    }, [sessionStatus, openModal, sortedGiftsDataList.length, onSubmit]);

    const breakpoints = {
        320: {
            slidesPerView: 1.75,
            spaceBetween: 20,
        },

        1280: {
            slidesPerView: 2.5,
            spaceBetween: 10,
        },
    };

    if (!simpleCrossSellProducts.length) return <></>;

    return (
        <div className="rounded-[1.25rem] bg-white px-5 pb-3 pt-5">
            <h3 className="text-[22px] font-extrabold leading-[25px] text-primary">Deal sốc hấp dẫn</h3>

            <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 bg-white lg:col-span-8 xl:col-span-9">
                    <Swiper slidesPerView={1.75} spaceBetween={10} breakpoints={breakpoints} className="pb-2! pt-3!">
                        <SwiperSlide>
                            <ProductCrossSellItem
                                item={mainProduct}
                                handleCheckboxChange={handleCheckboxChange}
                                isChecked={true}
                            />
                        </SwiperSlide>

                        {simpleCrossSellProducts?.map((item, idx) => {
                            const showPlus = idx !== simpleCrossSellProducts.length - 1;

                            return (
                                <SwiperSlide key={item.name}>
                                    <ProductCrossSellItem
                                        key={item.name}
                                        item={item}
                                        handleCheckboxChange={handleCheckboxChange}
                                        isChecked={selectedProducts.some(selectedItem => selectedItem.id === item.id)}
                                        showPlus={showPlus}
                                    />
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                </div>

                <ProductComboSummary
                    totalBasePrice={totalBasePrice}
                    totalPrice={totalPrice}
                    totalSavings={totalSavings}
                    onSubmit={handleBuyNowClick}
                    isLoading={isAddToCartLoading}
                />
            </div>

            {/* Gift selector for the main product — headless (renders null), always mounted so ref is ready */}
            <GiftsSelectorWidget
                ref={giftsSelectorRef}
                product={mainProduct}
                productQuantity={1}
                onConfirm={() => {
                    onSubmit();
                }}
            />
        </div>
    );
};

export default ProductCrossSell;
